import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type { ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';

import { StatusBadge } from '@/components/ui/status-badge';
import {
	LIMIT_POLICIES_LIST_QUERY,
	LIMIT_POLICY_DELETE_MUTATION
} from './limit-policies.gql.tsx';
import { LimitPolicyDetails } from './limit-policy-details';
import {
	selectLimitPolicyAfterSave,
	type LimitPolicyListNode
} from './limit-policy-list-selection';
import { cloneLimitPolicy } from './cloneLimitPolicy';

interface LimitPoliciesListResponse {
	limitPolicies?: {
		nodes?: LimitPolicyListNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

const LimitPolicyEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Shield className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const LimitPoliciesList = () => {
	const { t } = useTranslation('limitPolicies');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedPolicy, setSelectedPolicy] =
		useState<LimitPolicyListNode | null>(null);
	const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
	const [pendingDeletionSelection, setPendingDeletionSelection] =
		useState<LimitPolicyListNode | null>(null);
	const { showDialog } = useConfirmationDialog();
	const { goToCreate, goToEdit } = useResourceNavigation('limitPolicies');
	const go = useGo();

	const [deleteLimitPolicy, { loading: deleteLoading }] = useMutation(
		LIMIT_POLICY_DELETE_MUTATION
	);

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const { data, loading: isLoading, refetch, fetchMore } =
		useQuery<LimitPoliciesListResponse>(LIMIT_POLICIES_LIST_QUERY, {
			variables: {
				first: itemsToFetch
			}
		});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.limitPolicies?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.limitPolicies?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.limitPolicies) return prev;

					return {
						...prev,
						limitPolicies: {
							...fetchMoreResult.limitPolicies,
							nodes: [
								...(prev.limitPolicies?.nodes ?? []),
								...(fetchMoreResult.limitPolicies.nodes ?? [])
							]
						}
					};
				}
			});
			// Reset local limit policies to use server data after fetchMore
			setLocalLimitPolicies([]);
		} catch (error) {
			console.error('Error loading more limit policies:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const limitPoliciesData = data?.limitPolicies?.nodes ?? [];

	const handleRefresh = () => {
		setLocalLimitPolicies([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	// Local state to manage limit policy list data with imperative updates
	const [localLimitPolicies, setLocalLimitPolicies] = useState<
		LimitPolicyListNode[]
	>([]);

	const limitPolicies = useMemo(() => {
		const serverLimitPolicies = limitPoliciesData ?? [];
		// Always prefer server data - local state is only for optimistic updates
		if (localLimitPolicies.length === 0 || serverLimitPolicies.length === 0) {
			return serverLimitPolicies;
		}
		return localLimitPolicies;
	}, [limitPoliciesData, localLimitPolicies]);

	// Update local limit policies when query data changes (but only if no local override exists)
	useEffect(() => {
		if (
			!isLoading &&
			limitPoliciesData &&
			localLimitPolicies.length === 0
		) {
			setLocalLimitPolicies(limitPoliciesData);
		}
	}, [limitPoliciesData, isLoading, localLimitPolicies.length]);

	// Handle limit policy selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectLimitPolicyId?: string } | null;
		if (
			state?.selectLimitPolicyId &&
			limitPolicies.length > 0 &&
			!isLoading
		) {
			const limitPolicyIdToSelect = state.selectLimitPolicyId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectLimitPolicyAfterSave({
						limitPolicyId: limitPolicyIdToSelect,
						client: apolloClient,
						currentLimitPolicies: limitPolicies,
						onDataUpdate: setLocalLimitPolicies,
						onLimitPolicySelect: setSelectedPolicy,
						onOpenDetailsPanel: () => setDetailsPanelOpen(true)
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select limit policy after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		limitPolicies.length,
		isLoading,
		apolloClient,
		limitPolicies,
		navigate,
		location.pathname
	]);

	// Handle pending selection after delete and refresh
	useEffect(() => {
		if (pendingDeletionSelection && limitPoliciesData) {
			// Data has been refreshed after deletion, now handle selection
			handlePostDeletionSelection(
				pendingDeletionSelection,
				limitPoliciesData
			);
		}
	}, [limitPoliciesData, pendingDeletionSelection]);

	const handleRowClick = (policy: LimitPolicyListNode) => {
		setSelectedPolicy(policy);
		setDetailsPanelOpen(true);
	};

	const handleCloseDetails = () => {
		setDetailsPanelOpen(false);
		setSelectedPolicy(null);
	};

	// Helper function to find the next limit policy to select after deletion
	const findNextLimitPolicyToSelect = (
		deletedPolicy: LimitPolicyListNode,
		policyList: LimitPolicyListNode[]
	): LimitPolicyListNode | null => {
		const currentIndex = policyList.findIndex(
			p => p.limitPolicyId === deletedPolicy.limitPolicyId
		);

		if (policyList.length <= 1) {
			return null;
		}

		// Try to select the next policy (same index after deletion)
		if (currentIndex < policyList.length - 1) {
			return policyList[currentIndex + 1];
		}

		// If deleting the last policy, select the previous one
		if (currentIndex > 0) {
			return policyList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextPolicyToSelect: LimitPolicyListNode | null,
		updatedPolicies: LimitPolicyListNode[]
	) => {
		setLocalLimitPolicies([]);
		// First, clear the current selection completely
		setSelectedPolicy(null);
		setDetailsPanelOpen(false);

		// Then after a brief delay, set the next policy
		setTimeout(() => {
			if (nextPolicyToSelect && updatedPolicies.length > 0) {
				// Find the policy in the updated list by ID
				const updatedPolicy = updatedPolicies.find(
					p => p.limitPolicyId === nextPolicyToSelect?.limitPolicyId
				);

				if (updatedPolicy) {
					setSelectedPolicy(updatedPolicy);
					setDetailsPanelOpen(true);
				}
			}

			setPendingDeletionSelection(null);
		}, 100);
	};

	const handleDeleteLimitPolicy = async (
		limitPolicy: LimitPolicyListNode
	) => {
		const confirmed = await showDialog(
			'Delete Limit Policy',
			`Are you sure you want to delete limit policy "${limitPolicy.name}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Clear selection immediately if deleting the selected policy
			// if (wasSelectedPolicy) {
			setSelectedPolicy(null);
			setDetailsPanelOpen(false);
			// }

			try {
				const result = await deleteLimitPolicy({
					variables: {
						input: {
							limitPolicyId: limitPolicy.limitPolicyId
						}
					},
					refetchQueries: ['LimitPolicies']
				});

				const checkResult = result.data?.deleteLimitPolicy;

				if (checkResult?.success) {
					showMutationSuccessToast('limitPolicies', 'delete');
					const nextPolicyToSelect = findNextLimitPolicyToSelect(
						limitPolicy,
						limitPolicies
					);

					if (nextPolicyToSelect) {
						setPendingDeletionSelection(nextPolicyToSelect);
					}

					// Reset local limit policies to allow fresh data from refresh
					setLocalLimitPolicies([]);

					// Refresh the data table - useEffect will handle selection
					refetch();
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(
						`Failed to delete limit policy: ${errorMessage}`
					);
				}
			} catch (error) {
				console.error('Delete limit policy error:', error);
				toast.error('Failed to delete limit policy. Please try again.');
			}
		}
	};

	const handleDeleteLimitPolicyById = async (limitPolicyId: string) => {
		const limitPolicy = limitPolicies?.find(
			p => p.limitPolicyId === limitPolicyId
		);
		if (limitPolicy) {
			await handleDeleteLimitPolicy(limitPolicy);
		}
	};

	const handleCloneLimitPolicy = (limitPolicyId: string) => {
		const limitPolicy = limitPolicies?.find(
			p => p.limitPolicyId === limitPolicyId
		);
		if (limitPolicy) {
			const cloneData = cloneLimitPolicy({
				name: limitPolicy.name,
				priority: limitPolicy.priority,
				isActive: limitPolicy.isActive,
				scenarioId: limitPolicy.scenario?.scenarioId,
				scenarioLabel: limitPolicy.scenario?.name,
				limitId: limitPolicy.limit?.limitId,
				limitLabel: limitPolicy.limit?.name,
				billingCycleId: limitPolicy.billingCycle?.billingCycleId
			});
			go({
				to: '/limit-policies/create',
				state: { cloneData }
			});
		}
	};

	const columns: ColDef<LimitPolicyListNode>[] = useMemo(
		() => [
			{
				headerName: t('list.columns.name'),
				field: 'name',
				flex: 1,
				minWidth: 200
			},
			{
				headerName: t('list.columns.scenario'),
				colId: 'scenarioDescription',
				flex: 1,
				minWidth: 160,
				valueGetter: params => params.data?.scenario?.name ?? '—'
			},
			{
				headerName: t('list.columns.limit'),
				colId: 'limitDescription',
				flex: 1,
				minWidth: 160,
				valueGetter: params => params.data?.limit?.name ?? '—'
			},
			{
				headerName: t('list.columns.billingCycle'),
				colId: 'billingCycleName',
				flex: 0.6,
				minWidth: 140,
				valueGetter: params => params.data?.billingCycle?.name ?? '—'
			},
			{
				headerName: t('list.columns.priority'),
				field: 'priority',
				width: 110,
				minWidth: 110,
				cellClass: 'ag-right-aligned-cell',
				headerClass: 'ag-right-aligned-header',
				valueFormatter: params => `${params.value ?? ''}`
			},
			{
				headerName: t('list.columns.status'),
				field: 'isActive',
				width: 120,
				minWidth: 120,
				cellRenderer: ActiveInactiveCellRenderer
			},
			{
				headerName: '',
				field: undefined,
				width: 60,
				minWidth: 60,
				maxWidth: 60,
				cellRenderer: DeleteCellRenderer,
				cellRendererParams: {
					onDelete: handleDeleteLimitPolicy
				},
				sortable: false,
				filter: false,
				resizable: false
			}
		],
		[t, handleDeleteLimitPolicy]
	);

	return (
		<ListPageLayout
			columns={columns}
			data={limitPolicies || []}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={isLoading || deleteLoading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={handleRefresh}
			detailsPanelOpen={detailsPanelOpen}
			onCloseDetails={handleCloseDetails}
			selectedItem={selectedPolicy}
			detailsTitle="Limit Policy Details"
			onEdit={goToEdit}
			onDelete={handleDeleteLimitPolicyById}
			onClone={handleCloneLimitPolicy}
			renderDetailsContent={(policy: LimitPolicyListNode) => (
				<LimitPolicyDetails limitPolicyId={policy.limitPolicyId} />
			)}
			getRecordId={policy => policy.limitPolicyId}
			getDetailsDescription={policy => policy.name}
			getStatusBadge={policy => (
				<StatusBadge isActive={policy.isActive} />
			)}
			renderEntityIcon={() => <LimitPolicyEntityIcon />}
			getEntityTypeName={policy =>
				policy.isActive ? t('details.activePolicy') : t('details.inactivePolicy')
			}
			showCreateButton={true}
			showRefreshButton={true}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
};
